import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/joy';
import { DragDropContext } from '@hello-pangea/dnd';
import HomeIcon from '@mui/icons-material/Home';

import { useStorage } from '../../hooks/useStorage';
import { useSelection } from '../../hooks/useSelection';
import { useContextMenu } from '../../hooks/useContextMenu';

import FileList from './FileList';
import UploadButton from './UploadButton';
import { Toolbar, FileGrid, ContextMenu } from './components';
import {
  CreateFolderModal,
  DeleteConfirmModal,
  RenameModal,
  MoveToModal,
} from './modals';

const VIEW_STORAGE_KEY = 'storage-view-preference';

const StoragePage = () => {
  const {
    currentPrefix,
    breadcrumbs,
    files,
    folders,
    loading,
    isTruncated,
    uploading,
    uploadProgress,
    uploadingFileName,
    navigateToFolder,
    navigateToRoot,
    navigateToBreadcrumb,
    loadMore,
    uploadFile,
    downloadFile,
    deleteFile,
    createFolder,
    refresh,
    moveItems,
    renameItem,
  } = useStorage();

  // View state (grid or list)
  const [view, setView] = useState(() => {
    return localStorage.getItem(VIEW_STORAGE_KEY) || 'grid';
  });

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Combine folders and files
  const allItems = useMemo(() => [
    ...folders.map((f) => ({ ...f, type: 'folder' })),
    ...files.map((f) => ({ ...f, type: 'file' })),
  ], [folders, files]);

  // Filter items by search
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return allItems;
    const query = searchQuery.toLowerCase();
    return allItems.filter((item) =>
      item.name.toLowerCase().includes(query)
    );
  }, [allItems, searchQuery]);

  // Selection state
  const {
    selectedCount,
    isSelected,
    toggleSelect,
    selectAll,
    clearSelection,
    getSelectedItems,
    hasSelection,
  } = useSelection(filteredItems);

  // Context menu state
  const contextMenu = useContextMenu();

  // Modal states
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState(null);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [itemsToMove, setItemsToMove] = useState([]);
  const [renamingId, setRenamingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  // Persist view preference
  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [view]);

  // Clear selection when navigating
  useEffect(() => {
    clearSelection();
    setSearchQuery('');
  }, [currentPrefix]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle open (folder navigate or file download)
  const handleOpen = useCallback((item) => {
    if (item.type === 'folder') {
      navigateToFolder(item.path);
    } else {
      downloadFile(item.path, item.name);
    }
  }, [navigateToFolder, downloadFile]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Don't handle if in input/textarea or modal is open
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable ||
        createFolderOpen ||
        deleteModalOpen ||
        renameModalOpen ||
        moveModalOpen
      ) {
        return;
      }

      // Delete key - delete selected items
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (hasSelection) {
          event.preventDefault();
          const items = getSelectedItems();
          if (items.length > 0) {
            setItemsToDelete(items);
            setDeleteModalOpen(true);
          }
        }
      }

      // F2 - rename selected item (only if single selection)
      if (event.key === 'F2') {
        const items = getSelectedItems();
        if (items.length === 1) {
          event.preventDefault();
          setItemToRename(items[0]);
          setRenameModalOpen(true);
        }
      }

      // Enter - open selected item (only if single selection)
      if (event.key === 'Enter') {
        const items = getSelectedItems();
        if (items.length === 1) {
          event.preventDefault();
          handleOpen(items[0]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    hasSelection,
    getSelectedItems,
    createFolderOpen,
    deleteModalOpen,
    renameModalOpen,
    moveModalOpen,
    handleOpen,
  ]);

  // Handle folder creation
  const handleCreateFolder = async (folderName) => {
    await createFolder(folderName);
    setCreateFolderOpen(false);
  };

  // Handle single delete (from context menu)
  const handleDeleteClick = (item) => {
    setItemsToDelete([item]);
    setDeleteModalOpen(true);
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    const items = getSelectedItems();
    if (items.length > 0) {
      setItemsToDelete(items);
      setDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    for (const item of itemsToDelete) {
      let path = item.path;
      if (item.type === 'folder' && !path.endsWith('/')) {
        path += '/';
      }
      await deleteFile(path);
    }
    setDeleteModalOpen(false);
    setItemsToDelete([]);
    clearSelection();
  };

  // Handle file upload
  const handleUpload = async (fileList) => {
    for (const file of fileList) {
      await uploadFile(file);
    }
  };

  // Handle rename
  const handleRenameClick = (item) => {
    setItemToRename(item);
    setRenameModalOpen(true);
  };

  const handleRenameSubmit = async (newName) => {
    if (itemToRename) {
      await renameItem(itemToRename.path, newName);
      setRenameModalOpen(false);
      setItemToRename(null);
    }
  };

  // Handle inline rename
  const handleInlineRenameSubmit = async (oldPath, newName) => {
    await renameItem(oldPath, newName);
    setRenamingId(null);
  };

  // Handle move
  const handleMoveClick = (item) => {
    setItemsToMove([item]);
    setMoveModalOpen(true);
  };

  const handleBulkMove = () => {
    const items = getSelectedItems();
    if (items.length > 0) {
      setItemsToMove(items);
      setMoveModalOpen(true);
    }
  };

  const handleMoveSubmit = async (destinationPath) => {
    const paths = itemsToMove.map((item) => item.path);
    await moveItems(paths, destinationPath);
    setMoveModalOpen(false);
    setItemsToMove([]);
    clearSelection();
  };

  // Handle drag and drop
  const handleDragStart = useCallback(() => {
    // Could add visual feedback here
  }, []);

  const handleDragUpdate = useCallback((update) => {
    if (update.destination) {
      // Check if hovering over a folder
      const destIndex = update.destination.index;
      const destItem = filteredItems[destIndex];
      if (destItem?.type === 'folder' && destItem.path !== update.draggableId) {
        setDropTargetId(destItem.path);
      } else {
        setDropTargetId(null);
      }
    } else {
      setDropTargetId(null);
    }
  }, [filteredItems]);

  const handleDragEnd = useCallback(async (result) => {
    setDropTargetId(null);

    if (!result.destination) return;

    const sourceItem = filteredItems.find((item) => item.path === result.draggableId);
    if (!sourceItem) return;

    // Check if dropped on a folder
    const destIndex = result.destination.index;
    const destItem = filteredItems[destIndex];

    if (destItem?.type === 'folder' && destItem.path !== sourceItem.path) {
      // Get all selected items if the dragged item is selected
      const itemsToMove = isSelected(sourceItem.path)
        ? getSelectedItems()
        : [sourceItem];

      // Filter out the destination folder from items to move
      const filteredMoveItems = itemsToMove.filter(
        (item) => item.path !== destItem.path
      );

      if (filteredMoveItems.length > 0) {
        const paths = filteredMoveItems.map((item) => item.path);
        await moveItems(paths, destItem.path);
        clearSelection();
      }
    }
  }, [filteredItems, isSelected, getSelectedItems, moveItems, clearSelection]);

  // Check all selected
  const allSelected = filteredItems.length > 0 && selectedCount === filteredItems.length;
  const someSelected = selectedCount > 0 && selectedCount < filteredItems.length;

  const handleSelectAll = () => {
    if (allSelected) {
      clearSelection();
    } else {
      selectAll();
    }
  };

  const storageBreadcrumbs = (
    <Breadcrumbs separator="›" sx={{ px: 0 }}>
      <Link
        component="button"
        color={currentPrefix ? 'neutral' : 'primary'}
        onClick={navigateToRoot}
        underline="hover"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        <HomeIcon fontSize="small" />
        My Files
      </Link>
      {breadcrumbs.map((crumb, index) => (
        <Link
          key={crumb.path}
          component="button"
          color={index === breadcrumbs.length - 1 ? 'primary' : 'neutral'}
          onClick={() => navigateToBreadcrumb(crumb.path)}
          underline="hover"
        >
          {crumb.name}
        </Link>
      ))}
    </Breadcrumbs>
  );

  return (
    <Box
      component="main"
      className="MainContent"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        pt: '0.5%',
        px: 2,
        pb: 2,
        gap: 1,
      }}
    >
      {/* Toolbar */}
      <Toolbar
        breadcrumbs={storageBreadcrumbs}
        view={view}
        onViewChange={setView}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        onBulkDelete={handleBulkDelete}
        onBulkMove={handleBulkMove}
        onNewFolder={() => setCreateFolderOpen(true)}
        onRefresh={refresh}
        loading={loading}
        uploadButton={<UploadButton onUpload={handleUpload} disabled={uploading} />}
      />


      {/* Upload Progress */}
      {uploading && (
        <Box sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography level="body-sm">Uploading: {uploadingFileName}</Typography>
            <Typography level="body-sm">{uploadProgress}%</Typography>
          </Stack>
          <LinearProgress determinate value={uploadProgress} />
        </Box>
      )}

      {/* File Content */}
      <Box
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
        onClick={() => {
          clearSelection();
          contextMenu.hideMenu();
        }}
      >
        <DragDropContext
          onDragStart={handleDragStart}
          onDragUpdate={handleDragUpdate}
          onDragEnd={handleDragEnd}
        >
          {view === 'grid' ? (
            <FileGrid
              items={filteredItems}
              loading={loading}
              isSelected={isSelected}
              onSelect={toggleSelect}
              onOpen={handleOpen}
              onContextMenu={contextMenu.showMenu}
              renamingId={renamingId}
              onRenameSubmit={handleInlineRenameSubmit}
              onRenameCancel={() => setRenamingId(null)}
              dropTargetId={dropTargetId}
            />
          ) : (
            <FileList
              items={filteredItems}
              loading={loading}
              isSelected={isSelected}
              onSelectAll={handleSelectAll}
              allSelected={allSelected}
              someSelected={someSelected}
              onSelect={toggleSelect}
              onOpen={handleOpen}
              onDownload={downloadFile}
              onContextMenu={contextMenu.showMenu}
              renamingId={renamingId}
              onRenameSubmit={handleInlineRenameSubmit}
              onRenameCancel={() => setRenamingId(null)}
              dropTargetId={dropTargetId}
            />
          )}
        </DragDropContext>

        {/* Load More */}
        {isTruncated && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
            <Button variant="outlined" color="neutral" onClick={loadMore}>
              Load More
            </Button>
          </Box>
        )}
      </Box>

      {/* Context Menu */}
      <ContextMenu
        open={contextMenu.open}
        position={contextMenu.position}
        item={contextMenu.item}
        onOpen={handleOpen}
        onDownload={downloadFile}
        onRename={handleRenameClick}
        onMove={handleMoveClick}
        onDelete={handleDeleteClick}
        onClose={contextMenu.hideMenu}
      />

      {/* Modals */}
      <CreateFolderModal
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        onCreate={handleCreateFolder}
      />

      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemsToDelete([]);
        }}
        onConfirm={handleConfirmDelete}
        itemName={
          itemsToDelete.length === 1
            ? itemsToDelete[0]?.name
            : `${itemsToDelete.length} items`
        }
      />

      <RenameModal
        open={renameModalOpen}
        onClose={() => {
          setRenameModalOpen(false);
          setItemToRename(null);
        }}
        onRename={handleRenameSubmit}
        item={itemToRename}
      />

      <MoveToModal
        open={moveModalOpen}
        onClose={() => {
          setMoveModalOpen(false);
          setItemsToMove([]);
        }}
        onMove={handleMoveSubmit}
        items={itemsToMove}
        currentPath={currentPrefix}
      />
    </Box>
  );
};

export default StoragePage;
